import React, { useMemo } from 'react';
import { Header, LwButton, Page, SearchBar, Toolbox, useOnScrollDown } from 'redesign';
import { DEFAULT_PAGE_SIZE } from 'shared/utils/constants';
import { MonthDateControl } from 'shared/components/date-control-toolbox/month-date-control/month-date-control';
import { DateTime } from 'luxon';
import { useDebounce } from 'shared/hooks/use-debounce';
import { Box, styled } from '@mui/material';
import { RenderUnsettledUI } from 'shared/components';
import { useTranslation } from 'react-i18next';
import { AvailabilityCalendarGrid } from 'shared/components/availability/availability-calendar-grid';
import { IconAdd } from 'assets/img';
import { AvailabilityInfoButton } from './availability-info-button';
import { useAvailabilityActionHandler } from '../hooks/use-availability-action-handler';
import {
  AvailabilityPageParamsProvider,
  useAvailabilityPageParamsContext,
} from '../providers/availability-page-params-provider';
import { useGetAvailableWorkers } from '../hooks/use-get-workers-availability';
import { AvailabilitySidebarFormProvider } from '../wizard/availability-sidebar';
import { FilterButton } from '../../../../ogp/components/views/administration/shared';
import { AvailabilityFilterDataProvider } from '../filters/availability-filter-data-provider';
import { AppliedAvailabilityFilters } from '../filters/filters.types';
import { useSiteDetection } from '../../../hooks';

export const AvailabilityPageDataProvider = () => (
  <AvailabilityPageParamsProvider>
    <AvailabilityPage />
  </AvailabilityPageParamsProvider>
);

export const AvailabilityPage = () => {
  const { t } = useTranslation('all-platforms-common');
  const { filters, setFilters } = useAvailabilityPageParamsContext();
  const {
    isWriteSideBarOpen,
    showFilters,
    availabilityForEdit,
    handleEditAvailability,
    handleCreateAvailability,
    setIsWriteSideBarOpen,
    toggleFilters,
    mode,
  } = useAvailabilityActionHandler();

  const debouncedWorkerSearchValue = useDebounce(filters.search, 300);

  //May later support different modes, for now we hardcode to month
  const calendarMode = 'month';
  const { site } = useSiteDetection();

  const flexPoolIds = filters.flexPoolIds ?? [];
  const hasCompanyWithoutFlexpools = !!filters.allFlexpools && flexPoolIds.length === 0;

  const query = useGetAvailableWorkers({
    search: debouncedWorkerSearchValue,
    fromDate: filters.date ?? DateTime.now(),
    flexPoolIds,
    pageSizeParam: DEFAULT_PAGE_SIZE,
    enabled: !hasCompanyWithoutFlexpools,
  });

  const flatData = useMemo(() => {
    if (hasCompanyWithoutFlexpools) {
      return [];
    }
    return query.data?.pages.flatMap((data) => data.items) ?? [];
  }, [hasCompanyWithoutFlexpools, query.data]);

  const { fetchIfBottomScrolled } = useOnScrollDown({
    infiniteQueryResult: query,
    flatData,
  });

  const selectedDate = filters.date ?? DateTime.now();

  const handleChangeDate = (newDate: DateTime) => {
    setFilters({ date: newDate });
  };

  const handleChangeSearch = (search: string) => {
    setFilters({ search });
  };

  const handleChangeFilters = ({
    flexPoolIds,
    allFlexpoolsSelected,
    company,
  }: AppliedAvailabilityFilters) => {
    setFilters({
      flexPoolIds,
      companyId: company?.value,
      companyName: company?.label,
      allFlexpools: allFlexpoolsSelected || undefined,
    });
  };

  const currentCompany =
    filters.companyId && filters.companyName
      ? { value: filters.companyId, label: filters.companyName }
      : undefined;
  const shouldShowCalendar = query.status === 'success' || hasCompanyWithoutFlexpools;

  return (
    <Page
      header={
        <FlexBox>
          <Box>
            <Header titleText={t('availability_page.title')} />
          </Box>
          <CenteredBox>
            <MonthDateControl
              date={selectedDate}
              onChange={handleChangeDate}
              onToday={() => handleChangeDate(DateTime.now())}
              todayPosition="right"
            />
          </CenteredBox>
        </FlexBox>
      }
      toolbox={
        <Toolbox alignCenter>
          <LwButton
            onClick={handleCreateAvailability}
            startIcon={<IconAdd />}
            color="primary"
            iconColorMode="fill"
          >
            {t('availability_page.create_availability') || 'Create Availability'}
          </LwButton>
          <SearchBar
            onChange={handleChangeSearch}
            initialValue={filters.search}
            placeholder="Filter workers"
          />
          <StyledToolbarBox>
            {site !== 'partner' && <FilterButton onClick={toggleFilters} />}
            <AvailabilityInfoButton />
          </StyledToolbarBox>
        </Toolbox>
      }
    >
      <StyledCalendarWrapper>
        {shouldShowCalendar ? (
          <AvailabilityCalendarGrid
            flatData={flatData}
            selectedDate={selectedDate}
            calendarMode={calendarMode}
            onScroll={(e) => {
              if (e.target instanceof HTMLDivElement) {
                fetchIfBottomScrolled(e.target);
              }
            }}
            handleAvailabilityEditClick={handleEditAvailability}
          />
        ) : (
          RenderUnsettledUI({ data: query })
        )}
      </StyledCalendarWrapper>

      <AvailabilityFilterDataProvider
        handleFiltersChange={handleChangeFilters}
        isOpen={showFilters}
        toggleFilters={toggleFilters}
        currentFilters={filters.flexPoolIds || []}
        currentCompany={currentCompany}
        currentAllFlexpoolsSelected={!!filters.allFlexpools}
      />
      {isWriteSideBarOpen && (
        <AvailabilitySidebarFormProvider
          isOpen={isWriteSideBarOpen}
          onClose={() => setIsWriteSideBarOpen(false)}
          availabilityForEdit={availabilityForEdit}
          fromDate={selectedDate}
          searchTerm={debouncedWorkerSearchValue}
          mode={mode}
        />
      )}
    </Page>
  );
};

const FlexBox = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
}));

const CenteredBox = styled(Box)(() => ({
  position: 'absolute',
  left: '50%',
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
}));

const StyledCalendarWrapper = styled(Box)({
  height: '100%',
  width: '100%',
});

const StyledToolbarBox = styled(Box)(({ theme }) => ({
  marginLeft: 'auto',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-around',
  gap: theme.spacing(4),
}));
